// Editorial "featured collection" strip inside the cart drawer.
// Fetches once (on first open) then caches in-module for the session.
// Layout: vertical list on desktop (left column), horizontal scroller on mobile.

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { LocaleLink } from "@/components/LocaleLink";
import { fetchCollection, formatMoney, cardPriceFromProduct, type ShopifyProduct } from "@/lib/shopify";
import { shopifyImg, shopifySrcSet } from "@/lib/shopify-image";
import { useT } from "@/lib/i18n";

// Session-level cache so re-opening the drawer is instant.
const cache = new Map<string, ShopifyProduct[]>();

export type CartFeaturedConfig = {
  collectionHandle: string;
  heading: { da: string; en: string };
  intro: { da: string; en: string };
  seeAllHref: string; // locale-agnostic path; LocaleLink prefixes /en
  limit?: number;
};

export const DEFAULT_CART_FEATURED: CartFeaturedConfig = {
  collectionHandle: "fund-fra-japan",
  heading: { da: "Nye fund fra Japan", en: "New finds from Japan" },
  intro: {
    da: "Se vores nyeste fund fra den seneste rejse.",
    en: "Our latest finds from the trip.",
  },
  seeAllHref: "/collections/fund-fra-japan",
  limit: 4,
};

type Props = {
  config?: CartFeaturedConfig;
  layout?: "vertical" | "horizontal";
  onNavigate?: () => void;
};

export function CartFeaturedProducts({
  config = DEFAULT_CART_FEATURED,
  layout = "vertical",
  onNavigate,
}: Props) {
  const { locale } = useT();
  const [products, setProducts] = useState<ShopifyProduct[]>(
    () => cache.get(config.collectionHandle) ?? [],
  );
  const limit = config.limit ?? 4;

  useEffect(() => {
    let cancelled = false;
    if (cache.has(config.collectionHandle)) return;
    (async () => {
      const col = await fetchCollection(config.collectionHandle, limit + 4);
      if (cancelled) return;
      const items = (col?.products?.edges ?? []).slice(0, limit);
      cache.set(config.collectionHandle, items);
      setProducts(items);
    })();
    return () => {
      cancelled = true;
    };
  }, [config.collectionHandle, limit]);

  if (products.length === 0) return null;

  const heading = config.heading[locale] ?? config.heading.da;
  const intro = config.intro[locale] ?? config.intro.da;
  const seeAll = locale === "en" ? "See all" : "Se alle";

  return (
    <section
      aria-label={heading}
      className={layout === "vertical" ? "flex h-full flex-col" : "border-t border-border pt-5"}
    >
      <header className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-base leading-tight text-foreground">{heading}</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{intro}</p>
        </div>
        <LocaleLink
          to={config.seeAllHref}
          onClick={onNavigate}
          className="shrink-0 text-[11px] uppercase tracking-[0.18em] text-foreground/70 hover:text-terracotta"
        >
          {seeAll} <ArrowRight className="ml-1 inline h-3 w-3" aria-hidden="true" />
        </LocaleLink>
      </header>

      {layout === "vertical" ? (
        <ul className="mt-6 flex-1 space-y-7 overflow-y-auto pr-1">
          {products.map((p) => (
            <VerticalItem key={p.node.id} product={p} onNavigate={onNavigate} />
          ))}
        </ul>
      ) : (
        <ul
          className="mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1"
          style={{ scrollbarWidth: "none" }}
        >
          {products.map((p) => (
            <HorizontalItem key={p.node.id} product={p} onNavigate={onNavigate} />
          ))}
        </ul>
      )}
    </section>
  );
}

function VerticalItem({
  product,
  onNavigate,
}: {
  product: ShopifyProduct;
  onNavigate?: () => void;
}) {
  const node = product.node;
  const img = node.images.edges[0]?.node;
  const { price, isRange, isOnSale, compareAt } = cardPriceFromProduct(node);

  return (
    <li>
      <LocaleLink
        to="/products/$handle"
        params={{ handle: node.handle }}
        onClick={onNavigate}
        className="group flex gap-4"
      >
        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-sm bg-muted">
          {img && (
            <img
              src={shopifyImg(img.url, { width: 160 })}
              srcSet={shopifySrcSet(img.url, [128, 200, 320])}
              sizes="64px"
              alt={img.altText ?? node.title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          )}
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <h4 className="font-display text-[13px] leading-snug text-foreground line-clamp-2 group-hover:text-terracotta">
            {node.title}
          </h4>
          <span className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-xs">
            <span className={isOnSale ? "text-terracotta font-medium" : "text-foreground/80"}>
              {isRange ? `Fra ${formatMoney(price)}` : formatMoney(price)}
            </span>
            {isOnSale && compareAt && (
              <span className="text-[11px] text-muted-foreground line-through">
                {formatMoney(compareAt)}
              </span>
            )}
          </span>
        </div>
      </LocaleLink>
    </li>
  );
}

function HorizontalItem({
  product,
  onNavigate,
}: {
  product: ShopifyProduct;
  onNavigate?: () => void;
}) {
  const node = product.node;
  const img = node.images.edges[0]?.node;
  const { price, isRange, isOnSale, compareAt } = cardPriceFromProduct(node);

  return (
    <li className="w-32 flex-shrink-0 snap-start">
      <LocaleLink
        to="/products/$handle"
        params={{ handle: node.handle }}
        onClick={onNavigate}
        className="group block"
      >
        <div className="aspect-[4/5] overflow-hidden rounded-sm bg-muted">
          {img && (
            <img
              src={shopifyImg(img.url, { width: 260 })}
              srcSet={shopifySrcSet(img.url, [200, 260, 400])}
              sizes="128px"
              alt={img.altText ?? node.title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          )}
        </div>
        <h4 className="mt-2 font-display text-[12px] leading-snug text-foreground line-clamp-2 group-hover:text-terracotta">
          {node.title}
        </h4>
        <span className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-[11px]">
          <span className={isOnSale ? "text-terracotta font-medium" : "text-foreground/80"}>
            {isRange ? `Fra ${formatMoney(price)}` : formatMoney(price)}
          </span>
          {isOnSale && compareAt && (
            <span className="text-[10px] text-muted-foreground line-through">
              {formatMoney(compareAt)}
            </span>
          )}
        </span>
      </LocaleLink>
    </li>
  );
}
